import { ReactNode, ReactElement, ComponentProps } from "react";
import { FilterApplicableFor } from "../tags/types";
import { ComboboxOption, PopoverContent } from "@altimateai/lego";
import ColumnPicker from "../column/ColumnPicker";
import { FilterTextChangeValue } from "../filterDropdown";

export type SortDirection = "asc" | "desc" | "";

export const parseSortDirection = (
  value: string | null,
  fallback: SortDirection = ""
): SortDirection => (value === "asc" || value === "desc" || value === "" ? value : fallback);

export enum FilterType {
  DROPDOWN = "dropdown",
  TEXT = "text",
  DROPDOWN_SINGLE = "dropdown_single",
  MULTI_DROPDOWN = "multiDropdown",
  API_DROPDOWN = "apiDropdown",
  DATE = "date",
  DATE_RANGE = "dateRange",
}

/**
 * Check if a filter type supports multiple values
 * @param filterType - The filter type to check
 * @returns true if the filter type supports multiple values
 */
export const isMultiValueFilter = (filterType: FilterType): boolean => {
  return (
    filterType === FilterType.DROPDOWN ||
    filterType === FilterType.MULTI_DROPDOWN ||
    filterType === FilterType.API_DROPDOWN
  );
};

export type TextFilterMode = "bt" | "gt" | "lt" | { value: string };

export interface APIFilterParams {
  prefix: string;
  page: number;
  size?: number;
}

// Shared base — properties common to all filter variants (no filterType, no onChange)
export interface TableFilterCommon {
  id?: string;
  label?: string;
  searchKey?: string;
  /** @deprecated used for legacy filter dropdown */
  popoverContentProps?: ComponentProps<typeof PopoverContent>;
  /** Dynamic options callback for dependent filters.
   * Receives the current in-dialog filter selections and returns options.
   * When provided, the filter field remains visible even if static options are empty. */
  getOptions?: (activeFilters: FilterCondition[]) => ComboboxOption[];
}

export type ExtractedOption = {
  label: string | ReactNode;
  value: string;
  showTooltip?: boolean;
};

// DROPDOWN | API_DROPDOWN | MULTI_DROPDOWN — onChange always receives string[]
// TData: the shape returned by apiFunction, consumed by extractorFunction
export interface MultiValueTableFilter<TData = unknown> extends TableFilterCommon {
  filterType: FilterType.DROPDOWN | FilterType.API_DROPDOWN | FilterType.MULTI_DROPDOWN;
  onChange?: (value: string[]) => void;
  // optional, it wont be needed in new ui
  value?: string | string[];
  options?: (ComboboxOption & { subComponent?: (close: () => void) => ReactNode })[];
  searchBy?: "label" | "value";
  multiSelectFilterOpenOnHover?: boolean;
  multiSelectFilterOpenOnFirstHover?: boolean;
  // API_DROPDOWN specific
  apiFunction?: (params: APIFilterParams) => Promise<TData>;
  pageSize?: number;
  extractorFunction?: (data: TData) => ExtractedOption[];
  seeMore?: boolean;
}

/**
 * Helper that captures TData at construction time, ensuring apiFunction and
 * extractorFunction are type-compatible. The single cast lives here rather
 * than at every call site.
 */
export function createAPIDropdownFilter<TData>(
  config: MultiValueTableFilter<TData>
): MultiValueTableFilter {
  return config as unknown as MultiValueTableFilter;
}

// TEXT without filterMode — onChange receives string
export interface TextTableFilter extends TableFilterCommon {
  filterType: FilterType.TEXT;
  filterMode?: never;
  filterModeSearchKey?: never;
  onChange?: (value: string) => void;
  value?: string;
  placeHolder?: string;
  allowFractional?: boolean;
}

// TEXT with filterMode — onChange receives { value: string; mode: string }
interface TextTableFilterWithMode extends TableFilterCommon {
  filterType: FilterType.TEXT;
  filterMode: TextFilterMode;
  filterModeSearchKey: string;
  onChange?: (value: FilterTextChangeValue) => void;
  value?: string;
  placeHolder?: string;
  allowFractional?: boolean;
}

// DROPDOWN_SINGLE — onChange receives a single string
interface DropdownSingleTableFilter extends TableFilterCommon {
  filterType: FilterType.DROPDOWN_SINGLE;
  onChange?: (value: string) => void;
  value?: string;
  options?: (ComboboxOption & { subComponent?: (close: () => void) => ReactNode })[];
}

// DATE | DATE_RANGE — onChange receives string
interface DateTableFilter extends TableFilterCommon {
  filterType: FilterType.DATE | FilterType.DATE_RANGE;
  onChange?: (value: string) => void;
  value?: string;
}

export type TableFilter =
  | MultiValueTableFilter
  | TextTableFilter
  | TextTableFilterWithMode
  | DropdownSingleTableFilter
  | DateTableFilter;

export interface TableHeaderType<TData extends TableRowItem = TableRowItem> {
  id: number;
  label: string | ReactElement;
  width?: number | `${number}px` | `${number}%`; // Only allow numbers (px), "px" suffix, or "%" suffix
  /** Opt out of the 100px minimum-size floor. Use for narrow purpose-built
   *  columns (e.g. rank, checkbox) that explicitly need < 100px. */
  noFloor?: boolean;
  uid: string;
  filter?: TableFilter;
  tooltipInfo?: string | ReactNode;
  sort?: {
    value?: SortDirection;
    onChange?: (value: SortDirection) => void;
    /** URL param key for the sort column. Defaults to "sortAttribute". Set to enable declarative URL-based sorting. */
    sortSearchKey?: string;
    /** URL param key for the sort direction. Defaults to "sortOrder". */
    sortOrderSearchKey?: string;
    /** Value to store in URL search param for this column. Defaults to column uid. */
    sortParamValue?: string;
  };
  render: (item: TData) => ReactNode;
}

// Non-generic version for components that don't need to know about the data type
export type TableHeaderTypeBase = TableHeaderType<TableRowItem>;

export interface TableRowItem {
  id?: string | number;
  [key: string]: unknown;
}

export interface FilterCondition {
  field: string;
  value: string | string[] | { mode: string; value: string };
  logicalOperator?: "AND" | "OR";
}

export interface TablePortals {
  filter?: string;
  savedFilters?: string;
}

export interface DefaultSort {
  /** The sortParamValue (or column uid) to write to URL as the default sorted column */
  sortParamValue: string;
  /** Default sort direction */
  order: "asc" | "desc";
}

export interface TableProps<TData extends TableRowItem = TableRowItem> {
  header: TableHeaderType<TData>[];
  data: { items: TData[] };
  onRowClick?: (item: TData) => void;
  getRowUrl?: (item: TData) => string;
  getRowState?: (item: TData) => Record<string, unknown>;
  resetPage?: () => void;
  applicableFor?: FilterApplicableFor;
  headerClass?: string;
  tableClass?: string;
  tableBodyClass?: string;
  tableRowClass?: string;
  columnPickerPortal?: string;
  columnPickerProps?: ComponentProps<typeof ColumnPicker>;
  gridTemplateColumns?: string;
  idKey?: string | ((item: TData) => string);
  minWidth?: number;
  openedAccordion?: string;
  accordionComponent?: (item: TData) => React.ReactNode;
  enableRowSelection?: boolean;
  isLoading?: boolean;
  onRowSelect?: (rows: TData[]) => void;
  portals?: TablePortals;
  onFiltersChange?: (filters: FilterCondition[]) => void;
  /** Default sort for URL-based sorting. When set, DataTable writes this to URL on mount
   *  if no sort params exist, and resets to this when sort is cleared. */
  defaultSort?: DefaultSort;
  /** Opt out of the default "first column is sticky-left" pinning.
   *  Defaults to false to preserve existing tables' behavior. */
  disableColumnPinning?: boolean;
  /** Opt out of the viewport-fill / 500px-minimum height behavior.
   *  When true, the table renders at its natural content height. */
  disableDynamicHeight?: boolean;
  /** URL search-param keys to preserve when the user clicks "Clear filters".
   *  Pass route-level keys (date range, sort, granularity) so only filter
   *  tags are wiped and navigation state is kept. */
  clearFiltersKeysToPreserve?: string[];
}
